import React, { useState, useMemo, useEffect } from 'react';
import { GridLayout } from './lib/GridLayout';
import GridTable from './lib/GridTable';
import GridFormModal from './lib/GridFormModal';
import { Switch, DropDownOptions } from 'clad';

export const GridContext = React.createContext();

const Grid = props => {
    const {
        title,
        description,
        emptyPage,
        columns,
        actions,
        form,
        rowKey,
        activeColumn,
        headerStyle,
        titleStyle,
        url,
        onFetch,
        onActivate,
        activeCondition
    } = props;
    const { query, action } = props.query || {};
    const page =
        props.query && props.query.page
            ? parseInt(props.query.page, 10)
            : undefined;
    const id = props.query && props.query.id ? props.query.id : undefined;

    const [data, setData] = useState({ records: [], loading: true });
    const [statePagination, setStatePagination] = useState({
        pagination: { current: page || 1, pageSize: 25 },
        fetching: false
    });
    const [searchString, setSearchString] = useState(query || null);
    const [widthGrid, setWidthGrid] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [record, setRecord] = useState({});
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [redirect, setRedirect] = useState(false);

    const getWidthColumns = (columns, activeColumn) => {
        let widthColumns = activeColumn ? 150 : 100;
        columns.forEach(column => {
            widthColumns += column.width ? column.width : 0;
        });
        return widthColumns;
    };

    const widthColumns = useMemo(() => getWidthColumns(columns, activeColumn), [
        columns,
        activeColumn
    ]);

    const getGridColumns = (
        columns,
        actions,
        activeColumn,
        widthColumns,
        activeCondition
    ) => {
        const gridColumns = columns.slice();
        let actionsCol = {};
        let activeCol = {};

        const getActions = (text, record, index) =>
            actions.map(action => {
                if (action.condition && !action.condition(record)) {
                    return { key: '_exclude' };
                }
                if (action.contextRender) {
                    let context;
                    switch (action.key) {
                        case 'edit': {
                            context = action.contextRender(text, record, index);
                            return {
                                ...action,
                                ...context,
                                action: () =>
                                    handleEdit(
                                        text,
                                        record,
                                        index,
                                        context.action
                                    )
                            };
                        }
                        case 'duplicate': {
                            context = action.contextRender(text, record, index);
                            return {
                                ...action,
                                ...context,
                                action: () =>
                                    handleDuplicate(
                                        text,
                                        record,
                                        index,
                                        context.action
                                    )
                            };
                        }
                        case 'delete': {
                            context = action.contextRender(text, record, index);
                            return {
                                ...action,
                                ...context,
                                action: undefined,
                                confirm: {
                                    title: _(
                                        'Are you sure you want to delete %1?',
                                        record.name
                                    ),
                                    onConfirm: () =>
                                        handleDelete(
                                            text,
                                            record,
                                            index,
                                            context.action
                                        )
                                }
                            };
                        }
                        default: {
                            const refresh = action.refreshGrid
                                ? handleRefresh
                                : undefined;
                            context = action.contextRender(
                                text,
                                record,
                                index,
                                setMask,
                                refresh
                            );
                            return {
                                ...action,
                                ...context
                            };
                        }
                    }
                } else {
                    return action;
                }
            });

        if (actions) {
            actionsCol = {
                title: _('Actions'),
                key: 'action',
                width: 100,
                fixed: widthGrid > widthColumns ? false : 'right',
                render: (text, record, index) => {
                    const parseActions = getActions(text, record, index);
                    const options = parseActions.filter(
                        action => action.key !== '_exclude'
                    );

                    if (options.length > 0) {
                        return <DropDownOptions options={options} />;
                    }
                    return null;
                }
            };
        }

        if (activeColumn) {
            activeCol = {
                title: _('Active'),
                dataIndex: 'active',
                key: 'active',
                width: 50,
                fixed: widthGrid > widthColumns ? false : 'right',
                render: (text, record, index) => {
                    const parseActions = getActions(text, record, index);
                    const options = parseActions.filter(
                        action => action.key !== '_exclude'
                    );

                    if (activeCondition && !activeCondition(record, options)) {
                        return null;
                    }
                    return (
                        <Switch
                            checked={record.active === 1}
                            onChange={checked => {
                                handleActivate(
                                    checked,
                                    record,
                                    index,
                                    onActivate
                                );
                            }}
                        />
                    );
                }
            };
        }

        return [].concat(gridColumns, activeCol, actionsCol);
    };

    const gridColumns = getGridColumns(
        columns,
        actions,
        activeColumn,
        widthColumns,
        activeCondition
    );

    const handleRefresh = async () => {
        setStatePagination({
            ...statePagination,
            fetching: true
        });

        const result = await onFetch({
            query: searchString,
            pagination: { ...statePagination.pagination }
        });

        setData({
            records: result.data,
            count: result.count,
            loading: false
        });

        setStatePagination({
            ...statePagination,
            fetching: false
        });
    };

    const setMask = action => {
        setStatePagination({
            ...statePagination,
            fetching: action
        });
    };

    const handleActivate = async (checked, record, index, callback) => {
        setStatePagination({
            ...statePagination,
            fetching: true
        });
        var result = await callback.bind(null, checked, record)();

        if (result.ok) {
            await handleFetch(searchString, {
                ...statePagination.pagination
            });
            setStatePagination({
                ...statePagination,
                fetching: false
            });
        } else {
            setStatePagination({
                ...statePagination,
                fetching: false
            });
        }
    };

    const handleCancel = () => {
        if (url) {
            setTimeout(() => {
                setShowModal(false);
                setRecord({});
                window.history.pushState(null, 'List', `/#${url}`);
            }, 500);
        }
    };

    const handleCreate = () => {
        setShowModal(true);
        if (url) {
            window.history.pushState(null, 'Create', `/#${url}/create`);
        }
    };

    const handleEdit = async (text, record, index, callback) => {
        setShowModal(true);
        setLoadingRecord(true);
        if (url) {
            window.history.pushState(
                null,
                'Edit',
                `/#${url}/edit?id=${record.id}`
            );
        }
        const data = await callback();
        setRecord(data);
        setLoadingRecord(false);
    };

    const handleDuplicate = async (text, record, index, callback) => {
        setShowModal(true);
        setLoadingRecord(true);
        setRecord({ _duplicate: true });
        if (url) {
            window.history.pushState(null, 'Create', `/#${url}/create`);
        }
        const data = await callback();
        setRecord({ ...data, _duplicate: true });
        setLoadingRecord(false);
    };

    const handleDelete = async (text, record, index, callback) => {
        try {
            setStatePagination({
                ...statePagination,
                fetching: true
            });

            const result = await callback();

            if (result.ok) {
                try {
                    let result = await handleFetch(searchString, {
                        ...statePagination.pagination
                    });

                    if (result.data.length === 0 && result.count > 0) {
                        const newPagination = {
                            ...statePagination.pagination,
                            current: statePagination.pagination.current - 1
                        };

                        setRedirect(true);

                        result = await handleFetch(searchString, {
                            ...newPagination
                        });

                        setStatePagination({
                            pagination: {
                                ...statePagination.pagination,
                                current: statePagination.pagination.current - 1
                            },
                            fetching: false
                        });

                        const page = statePagination.pagination.current - 1;
                        const search =
                            searchString && searchString !== ''
                                ? `?query=${searchString}`
                                : '';
                        const pagination =
                            search && search !== ''
                                ? `&page=${page}`
                                : `?page=${page}`;
                        if (url) {
                            window.history.pushState(
                                null,
                                'Pagination',
                                `/#${url}${search}${pagination}`
                            );
                        }
                    } else {
                        setStatePagination({
                            pagination: {
                                ...statePagination.pagination
                            },
                            fetching: false
                        });
                    }
                } catch (err) {
                    setStatePagination({
                        ...statePagination,
                        fetching: false
                    });
                }
            } else {
                setStatePagination({
                    ...statePagination,
                    fetching: false
                });
            }
        } catch (err) {
            console.error(err);
            setStatePagination({
                ...statePagination,
                fetching: false
            });
        }
    };

    const handleSearch = async strSearch => {
        const initPagination = {
            ...statePagination.pagination,
            current: 1
        };

        setStatePagination({
            ...statePagination,
            fetching: true
        });

        setSearchString(strSearch);

        await handleFetch(strSearch);
        setStatePagination({
            pagination: initPagination,
            fetching: false
        });
        const search =
            strSearch && strSearch !== '' ? `?query=${strSearch}` : '';

        if (url) {
            window.history.pushState(null, 'Search', `/#${url}${search}`);
        }
    };

    const handlePagination = async (page, pageSize) => {
        const currentPagination = {
            current: page,
            pageSize: pageSize
        };
        try {
            setStatePagination({
                pagination: currentPagination,
                fetching: true
            });

            await handleFetch(searchString, { ...currentPagination });

            setStatePagination({
                pagination: {
                    ...currentPagination
                },
                fetching: false
            });

            const search =
                searchString && searchString !== ''
                    ? `?query=${searchString}`
                    : '';
            const pagination =
                search && search !== '' ? `&page=${page}` : `?page=${page}`;
            if (url) {
                window.history.pushState(
                    null,
                    'Pagination',
                    `/#${url}${search}${pagination}`
                );
            }
        } catch (err) {
            setStatePagination({
                pagination: {
                    ...currentPagination,
                    current: currentPagination.current - 1
                },
                fetching: false
            });
        }
    };

    useEffect(() => {
        if (action && action === 'create') {
            handleCreate();
        }
        if (action && action === 'edit') {
            actions.forEach(action => {
                if (action.contextRender && action.key === 'edit') {
                    const context = action.contextRender(
                        undefined,
                        { id },
                        undefined
                    );
                    handleEdit(undefined, { id }, undefined, context.action);
                }
            });
        }
        handleFetch(query, { current: page || 1, pageSize: 25 });
    }, []);

    const handleFetch = async (
        query = '',
        pagination = { current: 1, pageSize: 25 }
    ) => {
        try {
            let result = await onFetch({ query, pagination });

            setData({
                records: result.data,
                count: result.count,
                loading: false
            });

            return result;
        } catch (err) {
            console.error(err);
            setData({
                ...data,
                loading: false
            });
        }
    };

    return (
        <GridContext.Provider
            value={{
                statePagination,
                searchString,
                onPagination: handlePagination,
                onCreate: form ? handleCreate : null,
                onSearch: handleSearch,
                onRefresh: handleRefresh,
                total: data.count,
                records: data.records,
                redirect
            }}
        >
            <GridLayout
                title={title}
                description={description}
                loading={data.loading}
                emptyPage={emptyPage}
                headerStyle={headerStyle}
                titleStyle={titleStyle}
                setWidthGrid={setWidthGrid}
            >
                <GridTable
                    loading={data.loading}
                    fetching={statePagination.fetching}
                    records={data.records}
                    columns={gridColumns}
                    searchString={searchString}
                    emptyPage={emptyPage}
                    onCreate={form ? handleCreate : null}
                    onDelete={handleDelete}
                    rowKey={rowKey}
                    widthGrid={widthGrid}
                    widthColumns={widthColumns}
                    total={data.count}
                    redirect={redirect}
                />
                {form ? (
                    <GridFormModal
                        form={form}
                        onCancel={handleCancel}
                        visible={showModal}
                        record={record}
                        setRecord={setRecord}
                        loading={loadingRecord}
                        onRefresh={handleRefresh}
                    />
                ) : null}
            </GridLayout>
        </GridContext.Provider>
    );
};

export default Grid;
